import { useEffect } from 'react';
import { apiService, resolveStrapiMediaUrl } from '../services/api';

function unwrapMediaUrl(media: any): string {
  if (!media) return '';
  if (typeof media === 'string') return media;
  const raw =
    media.url ||
    media.data?.url ||
    media.data?.attributes?.url ||
    '';
  return typeof raw === 'string' ? raw : '';
}

function upsertLink(attrs: { rel: string; href: string; type?: string; sizes?: string; id: string }) {
  let el = document.getElementById(attrs.id) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.id = attrs.id;
    document.head.appendChild(el);
  }
  el.setAttribute('rel', attrs.rel);
  el.setAttribute('href', attrs.href);
  if (attrs.type) el.setAttribute('type', attrs.type);
  else el.removeAttribute('type');
  if (attrs.sizes) el.setAttribute('sizes', attrs.sizes);
  else el.removeAttribute('sizes');
}

function setMetaDescription(description: string) {
  let el = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', 'description');
    document.head.appendChild(el);
  }
  el.setAttribute('content', description);
}

/**
 * Applies Global Settings (site name, description, favicon from Strapi).
 * Static PNG/ICO in index.html stay as Chrome/Android fallbacks.
 */
const SiteMeta = () => {
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const response = await apiService.getGlobal();
        const data = (response as any)?.data ?? response;
        if (!data || cancelled) return;

        if (typeof data.siteName === 'string' && data.siteName.trim()) {
          document.title = data.siteName.trim();
        }
        if (typeof data.siteDescription === 'string' && data.siteDescription.trim()) {
          setMetaDescription(data.siteDescription.trim());
        }

        const faviconUrl = resolveStrapiMediaUrl(unwrapMediaUrl(data.favicon));
        if (!faviconUrl) return;

        const isSvg = /\.svg(\?|$)/i.test(faviconUrl);
        const isIco = /\.ico(\?|$)/i.test(faviconUrl);
        const type = isSvg ? 'image/svg+xml' : isIco ? 'image/x-icon' : 'image/png';

        upsertLink({ id: 'cms-favicon', rel: 'icon', href: faviconUrl, type });
        if (!isSvg) {
          upsertLink({
            id: 'cms-apple-touch-icon',
            rel: 'apple-touch-icon',
            href: faviconUrl,
            sizes: '180x180',
          });
        }
      } catch {
        /* keep static icons from index.html */
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  return null;
};

export default SiteMeta;
